import { CategoryIcon } from '@modules/common/components/CategoryIcon';
import { CreateNewReasonV3Modal } from '@modules/common/components/CreateNewReasonV3Modal';
import { trackCreateNewReasonEvents } from '@modules/common/components/CreateNewReasonV3Modal/helpers';
import { useShowCreateNewReasonInProgressDialog } from '@modules/common/components/CreateNewReasonV3Modal/hooks';
import { useMuiModalContext } from '@modules/common/components/Modal/ModalContext';
import { useNotificationContext } from '@modules/common/components/Notification/NotificationContext';
import { PredictionLabel } from '@modules/common/components/PredictionLabel';
import { useFetchSearchSuggestions } from '@modules/feed/hooks';
import { AddNewTrackedKeyword } from '@modules/feedback/components/AddNewTrackedKeyword';
import { AddPredictionToast } from '@modules/feedback/components/AddPredictions/AddPredictionToast';
import { AddReasonSuggestion } from '@modules/feedback/components/AddPredictions/AddReasonSuggestion';
import { AddTrackedKeyword } from '@modules/feedback/components/AddPredictions/AddTrackedKeyword';
import { getFeedbackLabels } from '@modules/feedback/components/FeedbackDetails/utils';
import { ReasonSuggestions } from '@modules/feedback/components/FeedbackDetails/utils/getReasonSuggestionsFromSuggestedReasons';
import {
  useGetFeedbackReasonLabels,
  useGetFeedbackTrackedKeywordLabels,
  useIsAddPredictionSearchV2Enabled,
  useUpdateEditedPredictions,
} from '@modules/feedback/hooks';
import { useFeedbackOnFeedbackStore } from '@modules/feedback/store';
import { FeedbackOnFeedbackActionType } from '@modules/feedback/store/feedbackOnFeedbackActionType';
import { trackFeedbackOnFeedbackV2Events } from '@modules/feedback/utils';
import AutoAwesomeRoundedIcon from '@mui/icons-material/AutoAwesomeRounded';
import KeyboardArrowRightRoundedIcon from '@mui/icons-material/KeyboardArrowRightRounded';
import {
  Autocomplete,
  AutocompleteRenderGroupParams,
  AutocompleteRenderInputParams,
  Box,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import { ManipulationType, SuggestionItem } from 'generated/graphql';
import {
  SyntheticEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { Feedback, FeedbackLabelType } from 'v2/core/entities/feedback';
import { ReasonOrEntityLabelType } from 'v2/core/entities/feedback/labels';
import { AddPredictionsAutocompleteV2 } from '../AddPredictionsAutocompleteV2';

const EMPTY_SUGGESTIONS: ReasonOrEntityLabelType[] = [];
const EMPTY_REASON_SUGGESTIONS: ReasonSuggestions[] = [];
const ADD_NEW_REASON_ID = 'ADD_NEW_REASON';
const ADD_NEW_TRACKED_KEYWORD_ID = 'ADD_NEW_TRACKED_KEYWORD';

interface AddPredictionsProps {
  isOpen: boolean;
  feedback: Feedback;
  reasonSuggestions?: ReasonSuggestions[];
  onAdditionOfReasonSuggestion?: () => void;
  onClose: () => void;
  isDetailView?: boolean;
  areReasonSuggestionsLoading: boolean;
}

export const AddPredictions = ({
  isOpen,
  feedback,
  reasonSuggestions = EMPTY_REASON_SUGGESTIONS,
  onAdditionOfReasonSuggestion: onAdditionOfReasonSuggestionProp,
  onClose,
  isDetailView = false,
  areReasonSuggestionsLoading,
}: AddPredictionsProps) => {
  const isAddPredictionSearchV2Enabled = useIsAddPredictionSearchV2Enabled();
  const { openModal } = useMuiModalContext();

  const [isCreateReasonModalOpen, setIsCreateReasonModalOpen] = useState(false);
  const { showCreateNewReasonInProgressDialog, isCreateNewReasonInProgress } =
    useShowCreateNewReasonInProgressDialog({
      closeExistingCreateNewReasonDialog: () => {
        setIsCreateReasonModalOpen(false);
      },
      openCreateNewReasonDialog: () => {
        setIsCreateReasonModalOpen(true);
      },
    });

  const [searchTerm, setSearchTerm] = useState('');
  const [selectedReasonDefinition, setSelectedReasonDefinition] =
    useState<string>();
  const [isReasonSuggestionSelected, setIsReasonSuggestionSelected] =
    useState(false);
  const [hasAddedReasonCandidate, setHasAddedReasonCandidate] = useState(false);
  const [selectedPrediction, setSelectedPrediction] = useState<
    ReasonOrEntityLabelType | undefined
  >();
  const getReasonSuggestionLabels = useCallback(
    () => reasonSuggestions.map((s) => s.label),
    [reasonSuggestions],
  );
  const [options, setOptions] = useState<ReasonOrEntityLabelType[]>(
    getReasonSuggestionLabels,
  );
  const [updatePredictions, setUpdatePredictions] = useState(false);
  const dispatch = useFeedbackOnFeedbackStore(({ dispatch }) => dispatch);
  const recordInEditMode = useFeedbackOnFeedbackStore(
    ({ recordInEditMode }) => recordInEditMode,
  );
  const [hasExactMatch, setHasExactMatch] = useState(false);
  const [isAddTrackedKeywordView, setIsAddTrackedKeywordView] = useState(false);
  const [isAddReasonSuggestionView, setIsAddReasonSuggestionView] =
    useState(false);

  const { showSuccessNotification } = useNotificationContext();
  const { entityLabels, reasonLabels } = useMemo(
    () => getFeedbackLabels(feedback.labels),
    [feedback.labels],
  );

  const feedbackReasonLabels = useGetFeedbackReasonLabels({
    labels: reasonLabels,
    feedbackID: feedback.id,
  });
  const feedbackTrackedKeywordLabels = useGetFeedbackTrackedKeywordLabels({
    labels: entityLabels,
    feedbackID: feedback.id,
  });

  const feedbackReasonLabelsRef = useRef(feedbackReasonLabels);
  const feedbackTrackedKeywordLabelsRef = useRef(feedbackTrackedKeywordLabels);

  const { debouncedSearchTerm, ...searchSuggestionsResponse } =
    useFetchSearchSuggestions(
      {
        searchTerm: searchTerm.trim(),
        include: [SuggestionItem.Reason, SuggestionItem.Entity],
      },
      isAddTrackedKeywordView || isAddReasonSuggestionView,
    );

  const { updateEditedPredictions } = useUpdateEditedPredictions(feedback.id);

  useEffect(() => {
    if (!searchTerm || isAddReasonSuggestionView) {
      setOptions(getReasonSuggestionLabels());
      return;
    }

    const searchReasonSuggestions: ReasonOrEntityLabelType[] =
      searchSuggestionsResponse.data?.getSearchSuggestions?.reasons
        ?.filter((reason) =>
          feedbackReasonLabelsRef.current.every(
            (reasonLabel) => reasonLabel.id !== reason.id,
          ),
        )
        .map((reason) => ({
          id: reason.id,
          displayName: reason.displayName,
          category: reason.metadata?.category,
          isVisible: true,
          type: FeedbackLabelType.Reason,
        })) ?? EMPTY_SUGGESTIONS;

    const searchTrackedKeywordSuggestions: ReasonOrEntityLabelType[] =
      searchSuggestionsResponse.data?.getSearchSuggestions?.entities
        ?.filter((entity) =>
          feedbackTrackedKeywordLabelsRef.current.every(
            (trackedKeywordLabel) => trackedKeywordLabel.id !== entity.id,
          ),
        )
        .map((trackedKeyword) => ({
          id: trackedKeyword.id,
          displayName: trackedKeyword.displayName,
          isVisible: true,
          type: FeedbackLabelType.Entity,
        })) ?? EMPTY_SUGGESTIONS;

    // Show Tracked Keyword options first if there is an exact match
    const exactTrackedKeywordMatchIndex =
      searchTrackedKeywordSuggestions.findIndex(
        (trackedKeyword) =>
          trackedKeyword.displayName.toLowerCase() === searchTerm.toLowerCase(),
      );

    if (exactTrackedKeywordMatchIndex !== -1) {
      const exactTrackedKeywordMatch =
        searchTrackedKeywordSuggestions[exactTrackedKeywordMatchIndex];

      searchTrackedKeywordSuggestions.splice(exactTrackedKeywordMatchIndex, 1);
      setOptions([
        exactTrackedKeywordMatch,
        ...searchReasonSuggestions,
        ...searchTrackedKeywordSuggestions,
      ]);
      setHasExactMatch(true);
      return;
    }

    setHasExactMatch(false);
    setOptions([
      ...searchReasonSuggestions,
      ...searchTrackedKeywordSuggestions,
    ]);
  }, [
    getReasonSuggestionLabels,
    isAddReasonSuggestionView,
    searchSuggestionsResponse.data?.getSearchSuggestions?.entities,
    searchSuggestionsResponse.data?.getSearchSuggestions?.reasons,
    searchTerm,
  ]);

  const onCreateNewTrackedKeyword = () => {
    openModal({
      contentComponent: () => (
        <AddNewTrackedKeyword
          feedbackID={feedback.id}
          name={searchTerm.trim()}
          onCreateNewTrackedKeyword={onClose}
          isDetailView={isDetailView}
        />
      ),
      modalProps: {
        fullWidth: true,
        maxWidth: 'lg',
        PaperProps: {
          elevation: 3,
        },
      },
    });
  };

  const onCreateNewReason = (_: SyntheticEvent, reasonName?: string) => {
    setSelectedReasonDefinition(reasonName);
    if (isCreateNewReasonInProgress) {
      showCreateNewReasonInProgressDialog();
      return;
    }

    setIsCreateReasonModalOpen(true);
    trackCreateNewReasonEvents({
      eventName: 'Create New Reason - Create New Reason Flow Triggered',
      selectedRC: reasonName,
      from: 'Feedback Record',
    });
  };

  const onSearchTermChange = (
    _: SyntheticEvent<Element, Event>,
    prediction: string | ReasonOrEntityLabelType | null,
  ) => {
    if (!prediction || typeof prediction === 'string') {
      return;
    }

    if (prediction.type === FeedbackLabelType.Reason && searchTerm.trim()) {
      dispatch({
        type: FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE,
        payload: {
          id: feedback.id,
          editPredictions: [
            ...(recordInEditMode?.editPredictions ?? []),
            {
              type: FeedbackLabelType.Reason,
              manipulationType: ManipulationType.Add,
              prediction,
            },
          ],
        },
      });
      setIsReasonSuggestionSelected(false);
      setSelectedPrediction(prediction);
      setUpdatePredictions(true);
      setSearchTerm('');
    }

    if (prediction.type === FeedbackLabelType.Entity) {
      setIsReasonSuggestionSelected(false);
      setIsAddTrackedKeywordView(true);
      setSelectedPrediction(prediction);
    }

    if (prediction.type === FeedbackLabelType.Reason) {
      setIsReasonSuggestionSelected(true);
      setIsAddReasonSuggestionView(true);
      setSelectedPrediction(prediction);
    }
  };

  const onAfterAddingTrackedKeyword = () => {
    setUpdatePredictions(true);
    setSearchTerm('');
    setIsAddTrackedKeywordView(false);
  };

  const onAfterAddingReasonSuggestion = (
    prediction: ReasonOrEntityLabelType,
    hasAddedReasonCandidate: boolean,
  ) => {
    if (prediction.type === FeedbackLabelType.Reason) {
      dispatch({
        type: FeedbackOnFeedbackActionType.SET_RECORD_IN_EDIT_MODE,
        payload: {
          id: feedback.id,
          editPredictions: [
            ...(recordInEditMode?.editPredictions ?? []),
            {
              type: FeedbackLabelType.Reason,
              manipulationType: ManipulationType.Add,
              prediction,
              isSuggested: true,
              relatedRCs: selectedReasonDefinition
                ? [selectedReasonDefinition]
                : undefined,
            },
          ],
        },
      });
    }
    onAdditionOfReasonSuggestionProp?.();
    setUpdatePredictions(true);
    setHasAddedReasonCandidate(hasAddedReasonCandidate);
    setSearchTerm('');

    onClose();
  };

  const selectedReasonRC =
    reasonSuggestions.find(
      (reasonSuggestion) =>
        reasonSuggestion.label.id === selectedPrediction?.id,
    )?.relatedRCs[0] ?? '';

  const onAfterEditedPredictions = useCallback(() => {
    trackFeedbackOnFeedbackV2Events({
      eventName: 'Prediction Added',
      feedbackID: feedback.id,
      toggleLocation: isDetailView
        ? 'Feedback Detail View'
        : 'Feedback List View',
      predictionType:
        selectedPrediction?.type === 'REASON' ? 'Reason' : 'Tracked Keyword',
      predictionName: selectedPrediction?.displayName,
      isSuggested: isReasonSuggestionSelected,
    });
    if (isReasonSuggestionSelected && onAdditionOfReasonSuggestionProp) {
      onAdditionOfReasonSuggestionProp();
    }
    if (!hasAddedReasonCandidate) {
      showSuccessNotification({
        contentComponent: () => (
          <AddPredictionToast
            prediction={selectedPrediction}
            hasAddedReasonCandidate={hasAddedReasonCandidate}
            selectedReasonRC={selectedReasonRC}
          />
        ),
        props: {
          timeout: hasAddedReasonCandidate ? 10000 : 5000,
        },
      });
    }
  }, [
    feedback.id,
    hasAddedReasonCandidate,
    isDetailView,
    isReasonSuggestionSelected,
    onAdditionOfReasonSuggestionProp,
    selectedPrediction,
    selectedReasonRC,
    showSuccessNotification,
  ]);

  useEffect(() => {
    if (updatePredictions) {
      setUpdatePredictions(false);
      setIsAddTrackedKeywordView(false);
      setIsAddReasonSuggestionView(false);
      updateEditedPredictions({
        onAfterEditedPredictions,
      });

      // TODO: Refactor and remove a lot of unnecessary states here
      // For reason candidate, success modal can show up and hence
      // we don't want to unmount the parent component.
      if (!hasAddedReasonCandidate) {
        onClose();
      }
    }
  }, [
    onAfterEditedPredictions,
    onClose,
    updateEditedPredictions,
    updatePredictions,
    hasAddedReasonCandidate,
  ]);

  const renderAutocompleteInput = (params: AutocompleteRenderInputParams) => (
    <TextField
      {...params}
      autoFocus
      fullWidth
      size="small"
      placeholder="+ Add New Prediction"
    />
  );

  const filterOptions = (options: ReasonOrEntityLabelType[]) => {
    const filteredOptions = options.filter((option) => {
      if (
        (isAddTrackedKeywordView || isAddReasonSuggestionView) &&
        debouncedSearchTerm !== option.displayName
      ) {
        return false;
      }

      return true;
    });

    if (!isAddTrackedKeywordView && !hasExactMatch) {
      const isTrackedKeywordAlreadyPresentInRecord =
        feedbackTrackedKeywordLabelsRef?.current?.some(
          (trackedKeywordLabel) =>
            trackedKeywordLabel.displayName === debouncedSearchTerm,
        );
      const isReasonAlreadyPresentInRecord =
        feedbackReasonLabelsRef?.current?.some(
          (reasonLabel) => reasonLabel.displayName === debouncedSearchTerm,
        );

      if (!isTrackedKeywordAlreadyPresentInRecord) {
        filteredOptions.push({
          id: ADD_NEW_TRACKED_KEYWORD_ID,
          displayName: `Create new Tracked Keyword`,
          isVisible: true,
          type: FeedbackLabelType.Entity,
        });
      }

      if (!isReasonAlreadyPresentInRecord) {
        filteredOptions.push({
          id: ADD_NEW_REASON_ID,
          displayName: `Create new Reason`,
          isVisible: true,
          type: FeedbackLabelType.Reason,
        });
      }
    }

    return filteredOptions;
  };

  const isLoading = searchSuggestionsResponse.loading;

  const renderAutocompleteOption = (
    props: React.HTMLAttributes<HTMLLIElement>,
    option: ReasonOrEntityLabelType,
  ) => {
    if (isLoading) {
      return null;
    }

    if (isAddTrackedKeywordView && searchTerm === option.displayName) {
      return (
        <AddTrackedKeyword
          feedbackID={feedback.id}
          option={option}
          onCancel={() => setIsAddTrackedKeywordView(false)}
          onAfterAddingTrackedKeyword={onAfterAddingTrackedKeyword}
        />
      );
    }

    if (isAddReasonSuggestionView && searchTerm === option.displayName) {
      return (
        <AddReasonSuggestion
          option={option}
          onCancel={() => {
            setSearchTerm('');
            setIsAddReasonSuggestionView(false);
          }}
          onAfterAddingReasonSuggestion={onAfterAddingReasonSuggestion}
          relatedRCs={
            reasonSuggestions.find(
              (reasonSuggestion) => reasonSuggestion.label.id === option.id,
            )?.relatedRCs
          }
        />
      );
    }

    if (option.id === ADD_NEW_TRACKED_KEYWORD_ID) {
      return (
        <MenuItem {...props} onClick={onCreateNewTrackedKeyword}>
          <Typography color="primary" whiteSpace="break-spaces">
            {option.displayName}
          </Typography>
        </MenuItem>
      );
    }

    if (option.id === ADD_NEW_REASON_ID) {
      return (
        <MenuItem {...props} onClick={onCreateNewReason}>
          <Typography color="primary" whiteSpace="break-spaces">
            {option.displayName}
          </Typography>
        </MenuItem>
      );
    }

    const isTrackedKeyword = option.type === FeedbackLabelType.Entity;
    const isReason = option.type === FeedbackLabelType.Reason;
    const isReasonCandidateSuggestion = isReason && !option.category;

    const shouldShowUncategorizedIcon =
      isTrackedKeyword || isReasonCandidateSuggestion;

    const onClick = (event: React.MouseEvent<HTMLLIElement, MouseEvent>) =>
      isReasonCandidateSuggestion
        ? onCreateNewReason(event, option.displayName)
        : props.onClick?.(event);

    return (
      <MenuItem
        {...props}
        onClick={onClick}
        sx={{
          justifyContent: 'space-between !important',
        }}
      >
        <Stack
          direction="row"
          spacing={1}
          alignItems={option.type === 'REASON' ? 'start !important' : 'center'}
        >
          {option.type === 'REASON' && option.category && (
            <Box>
              <CategoryIcon category={option.category} />
            </Box>
          )}
          {shouldShowUncategorizedIcon && (
            <Box
              sx={{
                mt: isTrackedKeyword ? undefined : 0.25,
                width: '16px',
                height: '16px',
                borderRadius: '50%',
                borderStyle: 'dashed',
                color: 'text.secondary',
                boxSizing: 'content-box',
              }}
            />
          )}
          <PredictionLabel
            label={option}
            subTitle={
              isReasonCandidateSuggestion ? 'Create this Reason' : undefined
            }
          />
        </Stack>
        {shouldShowUncategorizedIcon && (
          <KeyboardArrowRightRoundedIcon
            sx={{
              color: 'text.secondary',
            }}
          />
        )}
      </MenuItem>
    );
  };

  const renderAutocompleteGroup = (params: AutocompleteRenderGroupParams) => (
    <Box component="li" key={params.key}>
      <Stack direction="row" spacing={1} py={0.5} px={1} alignItems="center">
        <AutoAwesomeRoundedIcon
          fontSize="small"
          sx={{
            color: 'text.secondary',
          }}
        />
        <Typography color="text.secondary" variant="subtitle2">
          {params.group}
        </Typography>
      </Stack>
      <Stack>{params.children}</Stack>
    </Box>
  );

  return (
    <>
      {isAddPredictionSearchV2Enabled ? (
        <AddPredictionsAutocompleteV2
          onClose={onClose}
          isOpen={isOpen}
          feedbackID={feedback.id}
          isDetailView={isDetailView}
          reasonSuggestions={reasonSuggestions}
          feedbackLabels={feedback.labels}
          areReasonSuggestionsLoading={areReasonSuggestionsLoading}
        />
      ) : (
        isOpen && (
          <Autocomplete
            open={isOpen}
            autoComplete
            disablePortal
            disableClearable={!searchTerm.trim()}
            value={searchTerm}
            options={options}
            loading={isLoading}
            disabled={isAddTrackedKeywordView || isAddReasonSuggestionView}
            groupBy={searchTerm.trim() ? undefined : () => 'Suggestions'}
            renderGroup={renderAutocompleteGroup}
            // Hide `No Options` option on search-term absence
            freeSolo
            getOptionLabel={(option) => {
              return typeof option === 'string' ? option : option.displayName;
            }}
            renderOption={renderAutocompleteOption}
            isOptionEqualToValue={(option, value) => option.id === value?.id}
            onChange={onSearchTermChange}
            onInputChange={(_, value) => {
              setSearchTerm(value);
            }}
            renderInput={renderAutocompleteInput}
            componentsProps={{ paper: { elevation: 8 } }}
            filterOptions={filterOptions}
            ListboxProps={{
              sx: {
                padding: isLoading ? 0 : undefined,
                maxHeight:
                  isAddTrackedKeywordView || isAddReasonSuggestionView
                    ? '100%'
                    : '480px',
              },
            }}
          />
        )
      )}
      <CreateNewReasonV3Modal
        open={isCreateReasonModalOpen}
        onClose={() => setIsCreateReasonModalOpen(false)}
        selectedRC={
          selectedReasonDefinition
            ? { name: selectedReasonDefinition }
            : undefined
        }
        feedbackID={feedback.id}
        onCreateReason={({ reasonID, reasonName, reasonCategory }) => {
          onAfterAddingReasonSuggestion(
            {
              id: reasonID,
              isVisible: true,
              displayName: reasonName,
              category: reasonCategory,
              type: FeedbackLabelType.Reason,
            },
            true,
          );
        }}
      />
    </>
  );
};
